import { forwardRef, ReactNode } from 'react';
import { rd, RemoteData } from '@passionware/monads';
import { Skeleton } from 'v5/design-system/Skeleton';
import { cn } from 'v5/platform/dom/cn';
import { DimmedContainer } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';

export interface EntityListHeaderProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children' | 'title'> {
  title: string | RemoteData<string>;
  count?: RemoteData<number>;
}

export const EntityListHeader = forwardRef<
  HTMLDivElement,
  EntityListHeaderProps
>(({ title, count, className, ...rest }, ref) => {
  const countWithPlaceholder = rd.useLastWithPlaceholder(count ?? rd.ofIdle());

  return (
    <div
      ref={ref}
      className={cn('flex items-center gap-1 text-fg-default', className)}
      {...rest}
    >
      <span>
        {rd
          .journey(typeof title === 'string' ? rd.of(title) : title)
          .wait(<Skeleton className="w-32 h-lh" />)
          .catch(() => 'Resources')
          .get()}
      </span>
      {rd
        .fullJourney(countWithPlaceholder)
        .initially(null)
        .wait(<Skeleton className="ml-2.5 w-10 h-lh" />)
        .catch(() => null)
        .map((countValue: number) => (
          <DimmedContainer
            key="count"
            shouldDim={rd.isPlaceholderData(countWithPlaceholder)}
            className="inline-flex items-center"
          >
            <span className="text-fg-muted">({countValue})</span>
          </DimmedContainer>
        ))}
    </div>
  );
});

EntityListHeader.displayName = 'EntityListHeader';
